/**
 * 宿主（网页端 / 桌面端）判定的**唯一**入口。
 *
 * 桌面端在页面脚本执行**之前**注入 `window.__XTS_DESKTOP__`（见
 * `apps/desktop/src-tauri/src/desktop.rs` 的 `boot_script`），所以这里可以**同步**读，
 * 不需要 state/effect，也就不会出现"先渲染再隐藏"的闪烁。
 *
 * 用法上有一条原则（见 docs/DESKTOP.md §十）：
 *   - **能共用就不分叉**；
 *   - 真要分叉时，问"**能力**有没有"比问"我是不是桌面端"更耐用
 *     （例如将来浏览器也支持文件系统访问，能力函数不用改调用方）。
 *
 * 目前只有一处用户：主页右下角的版本号标识。
 */

/**
 * 宿主信息：与 `vite-env.d.ts` 里 `Window.__XTS_DESKTOP__` 的类型一致。
 * 额外允许 `null`：注入写了 null 也是"没有标记"（见下面的判断）。
 */
type HostInfo = Window['__XTS_DESKTOP__'] | null;

function currentHost(): HostInfo {
  return typeof window !== 'undefined' ? window.__XTS_DESKTOP__ : undefined;
}

/**
 * 是否运行在桌面端。
 *
 * 参数只为可测试性保留：生产调用一律不带参数，读的是注入的全局标记。
 * 判断本身是纯函数，所以可以在 node 环境下直接单测 ——
 * 本仓库的测试环境没有 jsdom，不该为了测这一行去引入它。
 *
 * 刻意用 `!== undefined && !== null` 而不是 `typeof host !== 'undefined'`：
 * 前者能挡住"注入写了 null"这种坏情况（`typeof null === 'object'` 会误判成桌面端）。
 */
export function isDesktopHost(host: HostInfo = currentHost()): boolean {
  return host !== undefined && host !== null;
}
